/** @nocodegenimport */
import { runtime, } from '@augloop/runtime-client';
/** @nocodegenimport */
import { runtimeALv1 } from '@augloop/alv1-runtime-client';
import { IdentityProvider } from '@augloop/schemas';
import { AnnotationActivationMessage, AnnotationActivationResponse, AnnotationReleaseMessage, AnnotationReleaseResponse, AnnotationResultsMessage, BridgeMessage, ErrorResponse, MicroSyncMessage, Response, SessionCloseMessage, SessionInitMessage, SyncMessage, SessionReconnectMessage, SessionConnectMessage, SessionDisconnectMessage, SubmittedCustomMessage, ServerAuthenticationState, ServerAuthenticationStateChangeMessage, SessionInitResponse, } from '@augloop/session-protocol';
import { SchemaObject } from '@augloop/types-core';
import { ModelDownloader } from '@augloop/model-downloader';
import { OnnxInferenceService } from '@augloop/onnx-inference-service';
import { LogCategory } from '@1js/office-online-telemetry/lib/buls/LogCategory';
import { TraceLevel } from '@1js/office-online-telemetry/lib/buls/TraceLevel';
import { ULS } from '@1js/office-online-telemetry/lib/externalLogger';
import { ExcelServerSessionInitMessage } from '@augloop/excel-session-protocol';
import { PowerPointSessionInitMessage } from '@augloop/powerpoint-session-protocol';
import { parseFlights } from '@augloop/workflow-flights';
const getDataFieldType = (obj) => {
    const stringDataFieldType = 0;
    const booleanDataFieldType = 1;
    const int64DataFieldType = 2;
    if (typeof obj === 'string') {
        return stringDataFieldType;
    }
    else if (typeof obj === 'boolean') {
        return booleanDataFieldType;
    }
    else if (typeof obj === 'number') {
        // Only integers are supported for now
        return int64DataFieldType;
    }
    ULS.sendTraceTag(0x2224d18d /* tag_8jngn */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Warning, 'Unsupported data field type');
    return undefined;
};
const convertDataFields = (data, prefix) => {
    const systemMetadataDataClassification = 4;
    const dataFields = [];
    for (const key of Object.keys(data)) {
        if (data[key] === undefined || data[key] === null) {
            continue;
        }
        const dataField = {
            name: prefix ? `${prefix}.${key}` : key,
            dataType: getDataFieldType(data[key]),
            value: data[key],
            // All of our client telemetry is (must be) SystemMetadata
            classification: systemMetadataDataClassification
        };
        dataFields.push(dataField);
    }
    return dataFields;
};
const convertTelemetryEvent = (tenantToken, eventName, data, contract, contractData, dataCategories, diagnosticLevel) => {
    const otelEvent = {
        telemetryProperties: { ariaTenantToken: tenantToken, nexusTenantToken: -1 },
        eventName: eventName.split('_').join('.'),
        dataFields: convertDataFields(data),
        eventFlags: {
            dataCategories: dataCategories,
            diagnosticLevel: diagnosticLevel,
            samplingPolicy: 1 /* SamplingPolicy.Measure */
        }
    };
    if (contract) {
        otelEvent.eventContract = {
            name: contract,
            dataFields: convertDataFields(contractData, contract.split('.').slice(-1)[0])
        };
    }
    return otelEvent;
};
// Imports to generate in ScriptSharp (not including SchemaObjects)
export { IdentityProvider, ServerAuthenticationState, };
const areLicenseFeaturesEnabled = (_licenseFeatures) => {
    // TODO: NYI
    return Promise.resolve(false);
};
export class AugLoopRuntimeManager {
    constructor() {
        this._telemetryAggregationIntervalSec = 30;
        this._eventHandlerDictionary = {};
        this._changeGateEnabledCallback = undefined;
        this._bridge = null;
        this._bridgeInterceptor = null;
        this._sessionCloseReason = null;
        this._sessionConnectParams = null;
        this._serverAuthenticationState = ServerAuthenticationState.NotAuthenticated;
        this._connectCallbacks = new Map();
        this._connectionStateCallbackToken = 0;
        this._settings = {
            sendConnectionParamsAcrossBridge: true,
        };
        this.initialize = (p) => {
            var _a;
            // Features in different slices may each call initialize, so this is an expected case
            if (this._runtimeInitPromise) {
                ULS.sendTraceTag(0x2224d18e /* tag_8jngo */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Info, 'Runtime already initialized');
                return;
            }
            ULS.sendTraceTag(0x2224d18f /* tag_8jngp */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Info, 'Start and initialize augloop runtime ...');
            this.checkParametersAndThrow('initialize', p);
            const modelDownloaderPromise = this.isFeatureEnabled('ModelDownloaderEbrake', 'None')
                .then(ebraked => {
                if (!ebraked) {
                    this.modelDownloaderFromCDN = (p.appName && p.downloadBaseUrl) || p.downloadPolymerBaseUrl ? new ModelDownloader(p.appName, p.downloadBaseUrl, p.downloadPolymerBaseUrl) : undefined;
                }
                else {
                    ULS.sendTraceTag(0x2129a006 /* tag_7k0ag */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Info, 'Resource downloader not created due to feature gate');
                }
            });
            this.inferenceServiceFactory = (p.appName && p.inferenceServiceVersion) ? OnnxInferenceService.createFactory(p.appName, p.inferenceServiceVersion) : undefined;
            this.excelServerParameters = p.excelServer;
            this.powerpointParameters = p.powerpoint;
            // Initialize host metadata and host callbacks
            const hostMetadata = {
                appName: p.appName,
                appPlatform: 'Web',
                appVersion: p.appVersion,
                uiLanguage: p.uiLanguage,
                releaseAudienceGroup: p.releaseAudienceGroup,
                releaseChannel: p.releaseChannel,
                releaseCustomAudience: p.releaseCustomAudience,
                releaseFork: p.releaseFork,
                sessionId: p.sessionId,
                flights: p.flights,
                privateMode: p.privateMode,
                disabledServiceGroups: p.disabledServiceGroups
            };
            const hostCallbacks = {
                onResult: this.onResult.bind(this),
                onAnnotationResult: this.onAnnotationResult.bind(this),
                sendTelemetryEvent: this.sendTelemetryEvent.bind(this),
                sendDiagnosticTrace: this.sendDiagnosticTrace.bind(this),
                requestAuthToken: this.requestAuthToken.bind(this),
                isFeatureEnabled: this.isFeatureEnabled.bind(this),
                isChangeGateEnabled: this.isChangeGateEnabled.bind(this),
                setSessionData: this.setSessionData.bind(this),
                areLicenseFeaturesEnabled,
                onInitSession: this.onInitSession.bind(this),
                overrideSessionInitMessage: this.overrideSessionInitMessage.bind(this)
            };
            const flightsChecker = parseFlights((_a = p.flights) !== null && _a !== void 0 ? _a : '');
            this._settings.sendConnectionParamsAcrossBridge = flightsChecker.getBooleanValue('SendConnectionParamsAcrossBridge', false);
            const runtimeInitPromise = modelDownloaderPromise
                .then(() => {
                return runtime.init(p.serviceUrl, hostMetadata, hostCallbacks, {
                    telemetryAggregationIntervalSec: this._telemetryAggregationIntervalSec,
                    modelDownloader: this.modelDownloaderFromCDN,
                    inferenceServiceFactory: this.inferenceServiceFactory,
                    isDeltaGeneratorEnabled: p.isDeltaGeneratorEnabled,
                    disableSyncDeltaSending: p.disableSyncDeltaSending,
                    syncDeltaTimeout: p.syncDeltaTimeout
                });
            })
                .then(() => {
                return runtimeALv1.init(p.serviceUrl, hostMetadata, hostCallbacks);
            });
            let sessionCreationOptions = p.sessionCreationOptions;
            if (!sessionCreationOptions) {
                sessionCreationOptions = {};
            }
            const hostServerAuthStateChange = sessionCreationOptions.onServerAuthenticationStateChangeCallback;
            sessionCreationOptions.onServerAuthenticationStateChangeCallback = (serverAuthenticationState) => {
                this._serverAuthenticationState = serverAuthenticationState;
                if (hostServerAuthStateChange) {
                    hostServerAuthStateChange(serverAuthenticationState);
                }
                if (this._bridge) {
                    this.sendBridgeMessage({
                        bridgeId: '*',
                        docSessionId: '*',
                        message: new ServerAuthenticationStateChangeMessage({ serverAuthenticationState: serverAuthenticationState })
                    });
                }
            };
            const sessionCloseCallback = sessionCreationOptions.onSessionClose;
            sessionCreationOptions.onSessionClose = (sessionCloseMessage) => {
                this._sessionCloseReason = sessionCloseMessage.reason;
                if (sessionCloseCallback) {
                    sessionCloseCallback(sessionCloseMessage);
                }
                if (this._bridge) {
                    this.sendBridgeMessage({
                        bridgeId: '*',
                        docSessionId: '*',
                        message: sessionCloseMessage,
                    });
                }
            };
            const sessionConnectCallback = sessionCreationOptions.onSessionConnect;
            sessionCreationOptions.onSessionConnect = (isSeedingRequired, sessionUrl, origin, authToken) => {
                if (sessionConnectCallback) {
                    sessionConnectCallback(isSeedingRequired, sessionUrl, origin, authToken);
                }
                this._connectCallbacks.forEach((connectCallback, _token) => {
                    connectCallback(isSeedingRequired, sessionUrl, origin, authToken);
                });
                if (this._bridge) {
                    if (!this._sessionConnectParams && this._settings.sendConnectionParamsAcrossBridge) {
                        this._sessionConnectParams = {
                            authToken: authToken,
                            isSeedingRequired: isSeedingRequired,
                            sessionUrl: sessionUrl,
                            origin: origin
                        };
                        this.sendSessionInitResponseToBridge('*', '*');
                    }
                    this.sendBridgeMessage({
                        bridgeId: '*',
                        docSessionId: '*',
                        message: new SessionConnectMessage({ isSeedingRequired, sessionUrl, origin, authToken }),
                    });
                }
            };
            const sessionReconnectCallback = sessionCreationOptions.onSessionReconnect;
            sessionCreationOptions.onSessionReconnect = () => {
                if (sessionReconnectCallback) {
                    sessionReconnectCallback();
                }
                if (this._bridge) {
                    this.sendBridgeMessage({
                        bridgeId: '*',
                        docSessionId: '*',
                        message: new SessionReconnectMessage(),
                    });
                }
            };
            const sessionDisconnectCallback = sessionCreationOptions.onSessionDisconnect;
            sessionCreationOptions.onSessionDisconnect = (error) => {
                if (sessionDisconnectCallback) {
                    sessionDisconnectCallback(error);
                }
                if (this._bridge) {
                    this._sessionConnectParams = undefined;
                    this.sendBridgeMessage({
                        bridgeId: '*',
                        docSessionId: '*',
                        message: new SessionDisconnectMessage({ error }),
                    });
                }
            };
            const hostClaimsChallengeCallback = sessionCreationOptions.onClaimsChallengeCallback;
            sessionCreationOptions.onClaimsChallengeCallback = (claimsChallengeMessage) => {
                if (hostClaimsChallengeCallback) {
                    hostClaimsChallengeCallback(claimsChallengeMessage);
                }
                if (this._bridge) {
                    this.sendBridgeMessage({
                        bridgeId: '*',
                        docSessionId: '*',
                        message: claimsChallengeMessage
                    });
                }
            };
            // Declare createSession and use it now to set _runtimeInitPromise synchronously so additional
            // calls to the AugLoopRuntimeManager are queued until _runtimeInitPromise resolves
            this.createSession = () => {
                this._runtimeInitPromise = runtimeInitPromise.then(() => {
                    const docSessionId = sessionCreationOptions && sessionCreationOptions.docSessionId || p.sessionId;
                    return runtime.createSession(Object.assign(Object.assign({}, sessionCreationOptions), { docSessionId }));
                });
            };
            this.createSession();
        };
        this.getSession = () => {
            return this._runtimeInitPromise;
        };
        this.setULSLogger = (p) => {
            this.checkParametersAndThrow('setULSLogger', p && p.ulsLogger);
            ULS.setLogger(p.ulsLogger);
        };
        this.setSendOTelEventCallback = (p) => {
            this.checkParametersAndPropertyAndThrow('setSendOTelEventCallback', p && p.sendOTelEvent, this._sendOTelEventCallback);
            this._sendOTelEventCallback = p.sendOTelEvent;
        };
        this.setSendDiagnosticTraceCallback = (p) => {
            this.checkParametersAndPropertyAndThrow('setSendDiagnosticTraceCallback', p && p.sendDiagnosticTraceCallback, this._sendDiagnosticTraceCallback);
            this._sendDiagnosticTraceCallback = p.sendDiagnosticTraceCallback;
        };
        this.setRequestAuthTokenCallback = (p) => {
            this.checkParametersAndPropertyAndThrow('setRequestAuthTokenCallback', p && p.requestAuthTokenCallback, this._requestAuthTokenCallback);
            this._requestAuthTokenCallback = p.requestAuthTokenCallback;
        };
        this.setIsFeatureEnabledCallback = (p) => {
            this.checkParametersAndPropertyAndThrow('setIsFeatureEnabledCallback', p && p.featureEnabledCallback, this._featureEnabledCallback);
            this._featureEnabledCallback = p.featureEnabledCallback;
        };
        this.setIsChangeGateEnabledCallback = (p) => {
            this.checkParametersAndPropertyAndThrow('setIsChangeGateEnabledCallback', p && p.changeGateEnabledCallback, this._changeGateEnabledCallback);
            // ICM 494378846: Office Online apps have implemented this wrong causing AL crashes. It was used for the first time on 04/12/2024 in a not so critical feature.
            // It is Ok to turn off till apps put in a fix to mitigate the crashes.
            // this._changeGateEnabledCallback = p.changeGateEnabledCallback;
        };
        /************* START STATEFUL AUGMENTATION LOOP METHODS *************/
        this.registerLocalWorkflow = (p) => {
            const functionName = 'registerLocalWorkflow';
            this.checkParametersAndThrow(functionName, p && p.workflow);
            this.checkRuntimeInitAndThrow(functionName);
            void this._runtimeInitPromise.then((session) => {
                session.registerLocalWorkflow(p.workflow);
            });
        };
        this.activateAnnotation = (p) => {
            const functionName = 'activateAnnotation';
            const error = this.checkParameters(functionName, p && p.annotationType) || this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return this._runtimeInitPromise.then(session => {
                let options;
                if (p.config || p.handler || p.stateUpdateHandler) {
                    options = { config: p.config, callback: p.handler, stateUpdateCallback: p.stateUpdateHandler, forceReturnCachedAnnotations: p.forceReturnCachedAnnotations };
                }
                return session.activateAnnotation(p.annotationType, options);
            });
        };
        this.updateAnnotationConfig = (p) => {
            const functionName = 'updateAnnotationConfig';
            const error = this.checkParameters(functionName, p && p.token && p.config) || this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return this._runtimeInitPromise.then(session => {
                session.updateAnnotationConfig(p.token, p.config);
            });
        };
        this.releaseAnnotation = (p) => {
            const functionName = 'releaseAnnotation';
            const error = this.checkParameters(functionName, p && p.token) || this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return this._runtimeInitPromise.then(session => {
                return session.releaseAnnotation(p.token);
            });
        };
        this.submitOperation = (p) => {
            const functionName = 'submitOperation';
            this.checkParametersAndThrow(functionName, p && p.operation);
            this.checkRuntimeInit(functionName);
            void this._runtimeInitPromise.then(session => {
                session.submitOperation(p.operation, p.cv);
            });
        };
        this.setSessionDataCallback = (p) => {
            this.checkParametersAndPropertyAndThrow('setSessionDataCallback', p && (p.sessionDataCallback || p.handler), this._setSessionDataCallback);
            if (p.sessionDataCallback) {
                this._setSessionDataCallback = p.sessionDataCallback;
            }
            else if (p.handler) {
                this._setSessionDataCallback = (sessionUrl, origin, _authToken) => p.handler(sessionUrl, origin);
            }
        };
        /**
         * @deprecated use activateAnnotation with a handler parameter instead
         */
        this.setAnnotationResultCallback = (p) => {
            // TODO: Throw if this function has been called already, i.e., replace this code block with:
            // this.checkParametersAndPropertyAndThrow('setAnnotationResultCallback', p && p.handler, this._annotationResultCallback);
            const functionName = 'setAnnotationResultCallback';
            this.checkParametersAndThrow(functionName, p && p.handler);
            if (this._annotationResultCallback) {
                const errorMessage = `${functionName} already called`;
                ULS.sendTraceTag(0x200816c4 /* tag_6cb1e */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Error, errorMessage);
            }
            this._annotationResultCallback = p.handler;
        };
        this.setAnnotationState = (p) => {
            const functionName = 'setAnnotationState';
            this.checkParametersAndThrow(functionName, p && p.parentPath && p.id && p.value);
            this.checkRuntimeInitAndThrow(functionName);
            void this._runtimeInitPromise.then(session => {
                session.setAnnotationState(p.parentPath, p.id, p.value);
            });
        };
        this.setAnnotationMetadata = (p) => {
            const functionName = 'setAnnotationMetadata';
            this.checkParametersAndThrow(functionName, p && p.parentPath && p.id && p.value);
            this.checkRuntimeInitAndThrow(functionName);
            void this._runtimeInitPromise.then(session => {
                session.setAnnotationMetadata(p.parentPath, p.id, p.value);
            });
        };
        this.setInitSessionCallback = (p) => {
            this.checkParametersAndPropertyAndThrow('setInitSessionCallback', p && p.initSessionCallback, this._initSessionCallback);
            this._initSessionCallback = p.initSessionCallback;
        };
        this.setSessionInitOverrideCallback = (p) => {
            this.checkParametersAndPropertyAndThrow('setSessionInitOverrideCallback', p && p.handler, this._sessionInitOverrideCallback);
            this._sessionInitOverrideCallback = p.handler;
        };
        this.setMessageBridge = (p) => {
            this.checkParametersAndPropertyAndThrow('setMessageBridge', p && p.bridge, this._bridge);
            this._bridge = p.bridge;
            this._bridge.registerMessageReceivedCallback(this.onBridgeMessage.bind(this));
        };
        this.setMessageBridgeToALInterceptor = (p) => {
            this.checkParametersAndPropertyAndThrow('setMessageBridgeToALInterceptor', p && p.bridge, this._bridgeInterceptor);
            this._bridgeInterceptor = p.bridge;
            this._bridgeInterceptor.registerMessageReceivedCallback(this.onBridgeMessageFromInterceptor.bind(this));
        };
        this.forceReconnectSession = (p) => {
            const functionName = 'forceReconnectSession';
            const error = this.checkParameters(functionName, p) || this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return this._runtimeInitPromise.then(session => {
                if (p.excelServerParameters) {
                    this.excelServerParameters = p.excelServerParameters;
                }
                return session.forceReconnect();
            });
        };
        this.setSessionCloseCallback = (p) => {
            const functionName = 'setSessionCloseCallback';
            const error = this.checkParameters(functionName, p && p.callback) || this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return this._runtimeInitPromise.then(session => {
                session.setSessionCloseCallback(p.callback);
            });
        };
        this.setConnectCallback = (p) => {
            const token = this.getSessionStateCallbackToken('connect');
            if (p.callback) {
                this._connectCallbacks.set(token, p.callback);
            }
            return token;
        };
        this.removeConnectionStateCallback = (p) => {
            if (this._connectCallbacks.has(p.token)) {
                this._connectCallbacks.delete(p.token);
                return true;
            }
            return false;
        };
        this.submitCustomMessage = (p) => {
            const functionName = 'submitCustomMessage';
            const error = this.checkParameters(functionName, p && p.message) || this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return this._runtimeInitPromise.then(session => {
                return session.submitCustomMessage(p.message);
            });
        };
        this.submitLargeBinaryDataMessage = (p) => {
            const functionName = 'submitLargeBinaryDataMessage';
            const error = this.checkParameters(functionName, p && p.message) || this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            if (!MicroSyncMessage.typeGuard(p.message)) {
                return Promise.reject(`${functionName} only supports MicroSyncMessage`);
            }
            return this._runtimeInitPromise.then(session => {
                return session.submitLargeBinaryDataMessage(p.message);
            });
        };
        this.requestBinaryDataForBlob = (p) => {
            const functionName = 'requestBinaryDataForBlob';
            const error = this.checkParameters(functionName, p && p.blob) || this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return this._runtimeInitPromise.then(session => {
                return session.requestBinaryDataForBlob(p.blob);
            });
        };
        this.closeSession = () => {
            const error = this.checkRuntimeInit('closeSession');
            if (error) {
                return Promise.reject(error);
            }
            return this._runtimeInitPromise.then(session => {
                session.close();
            });
        };
        this.startNewSession = () => {
            if (!this.createSession) {
                const errorMessage = `startNewSession called before runtime init`;
                ULS.sendTraceTag(0x2008324a /* tag_6cdjk */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Error, errorMessage);
                throw new Error(errorMessage);
            }
            this.createSession();
            // reset cached session close reason
            this._sessionCloseReason = null;
            this._sessionConnectParams = null;
        };
        /************* END OF STATEFUL AUGMENTATION LOOP METHODS *************/
        /************* START STATELESS AUGMENTATION LOOP METHODS *************/
        this.submit = (p) => {
            const functionName = 'submit';
            this.checkParametersAndThrow(functionName, p && p.inputSchema && p.input);
            this.checkRuntimeInitAndThrow(functionName);
            void this._runtimeInitPromise.then(() => {
                runtimeALv1.submit(p.inputSchema, p.input, p.correlationVector);
            });
        };
        this.addEventHandler = (p) => {
            this.checkParametersAndThrow('addEventHandler', p && p.schemaName && p.handler);
            const eventHandlers = this._eventHandlerDictionary[p.schemaName] || [];
            eventHandlers.push(p.handler);
            this._eventHandlerDictionary[p.schemaName] = eventHandlers;
        };
        this.removeEventHandler = (p) => {
            this.checkParametersAndThrow('removeEventHandler', p && p.schemaName && p.handler);
            const handlers = this._eventHandlerDictionary[p.schemaName];
            if (!Array.isArray(handlers)) {
                return;
            }
            const index = handlers.indexOf(p.handler);
            if (index >= 0) {
                handlers.splice(index, 1);
            }
        };
        this.registerSimpleRemoteWorkflow = (p) => {
            const functionName = 'registerSimpleRemoteWorkflow';
            const error = this.checkParameters(functionName, p && p.name && p.lambdaRemote) || this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return this._runtimeInitPromise.then(() => {
                return runtimeALv1.registerSimpleRemoteWorkflow(p.name, p.lambdaBefore, p.lambdaRemote, p.lambdaAfter);
            });
        };
        this.registerSimpleLocalWorkflow = (p) => {
            const functionName = 'registerSimpleLocalWorkflow';
            const error = this.checkParameters(functionName, p && p.name && p.lambda) || this.checkRuntimeInit(functionName);
            if (error) {
                return Promise.reject(error);
            }
            return this._runtimeInitPromise.then(() => {
                return runtimeALv1.registerSimpleLocalWorkflow(p.name, p.lambda);
            });
        };
        this.registerReduceWorkflow = (_p) => {
            // Stubbed out ALv1 method, removal is tracked by:
            // Task 5082272: Finish removing references to ALv1 in AugLoopRuntimeManager.ts once all WAC references are removed
            return Promise.resolve();
        };
        /************* END OF STATELESS AUGMENTATION LOOP METHODS *************/
        /************* START DEPRECATED AUGMENTATION LOOP METHODS *************/
        this.registerSchemas = (_p) => {
            // Stubbed out ALv1 method, removal is tracked by:
            // Task 5082272: Finish removing references to ALv1 in AugLoopRuntimeManager.ts once all WAC references are removed
            return Promise.resolve();
        };
        /************* END OF DEPRECATED AUGMENTATION LOOP METHODS *************/
        this.onResult = (inputSchema, input, outputSchema, output) => {
            const eventHandlers = this._eventHandlerDictionary[outputSchema];
            if (!eventHandlers) {
                return;
            }
            for (const handler of eventHandlers) {
                if (handler) {
                    const result = {
                        inputSchema,
                        input,
                        outputSchema,
                        output
                    };
                    handler(result);
                }
            }
        };
        this.onAnnotationResult = (operation, cv) => {
            if (this._annotationResultCallback) {
                this._annotationResultCallback(operation, cv);
            }
        };
        this.requestAuthToken = (tokenRequest) => {
            if (!this._requestAuthTokenCallback) {
                return Promise.resolve({ Token: '' });
            }
            const tokenCallback = this._requestAuthTokenCallback(tokenRequest) || Promise.resolve('');
            return tokenCallback.then(tokenValue => {
                const token = tokenValue || '';
                return Promise.resolve({ Token: token });
            });
        };
        this.sendTelemetryEvent = (tenant, eventName, data, contract, contractData, _critical, dataCategories, diagnosticLevel) => {
            if (this._sendOTelEventCallback) {
                this._sendOTelEventCallback(convertTelemetryEvent(tenant, eventName, data, contract, contractData, dataCategories, diagnosticLevel));
            }
        };
        this.sendDiagnosticTrace = (tagId, level, message) => {
            if (this._sendDiagnosticTraceCallback) {
                this._sendDiagnosticTraceCallback(tagId, level, message);
            }
        };
        this.isFeatureEnabled = (feature, _scope) => {
            if (!feature) {
                return Promise.resolve(false);
            }
            const preName = 'Microsoft.Office.AugLoop.';
            if (feature.indexOf(preName) === 0 && this._featureEnabledCallback) {
                const featureName = feature.slice(preName.length);
                return Promise.resolve(this._featureEnabledCallback(featureName) || this._featureEnabledCallback(feature));
            }
            return Promise.resolve(false);
        };
        this.isChangeGateEnabled = (changeGateName) => {
            if (!changeGateName || !this._changeGateEnabledCallback) {
                return Promise.resolve(false);
            }
            return Promise.resolve(this._changeGateEnabledCallback(changeGateName));
        };
        this.setSessionData = (sessionUrl, origin, authToken) => {
            if (this._setSessionDataCallback) {
                this._setSessionDataCallback(sessionUrl, origin, authToken);
            }
        };
        this.overrideSessionInitMessage = (sessionInitMessage) => {
            if (this._sessionInitOverrideCallback) {
                return this._sessionInitOverrideCallback(sessionInitMessage);
            }
            if (sessionInitMessage.clientMetadata.appName.indexOf('Excel') === 0) {
                if (this.excelServerParameters) {
                    return this.overrideExcelSessionInitMessage(sessionInitMessage);
                }
            }
            if (sessionInitMessage.clientMetadata.appName.indexOf('PowerPoint') === 0) {
                if (this.powerpointParameters) {
                    return this.overridePowerPointSessionInitMessage(sessionInitMessage);
                }
            }
            return sessionInitMessage;
        };
        this.overrideExcelSessionInitMessage = (sessionInitMessage) => {
            const initMessage = {
                cluster: this.excelServerParameters.wacCluster,
                restUrl: this.excelServerParameters.ecsRestUrl,
                ecsId: this.excelServerParameters.ecsSessionId,
                ecsOperationUrl: this.excelServerParameters.ecsOperationUrl,
                workbookMetadataVersion: this.excelServerParameters.metadataVersion,
                serverEventVersion: this.excelServerParameters.serverEventVersion,
                permissionFlags: this.excelServerParameters.permissionFlags,
                configurations: this.excelServerParameters.configurations,
                collabUserListVersion: this.excelServerParameters.userListVersion,
                collabStateId: this.excelServerParameters.collabStateId,
                accessToken: this.excelServerParameters.accessToken,
                accessTokenTTL: this.excelServerParameters.accessTokenTTL,
                supportCustomMessages: this.excelServerParameters.supportCustomMessages
            };
            for (const key of Object.keys(sessionInitMessage)) {
                initMessage[key] = sessionInitMessage[key];
            }
            return new ExcelServerSessionInitMessage(initMessage);
        };
        this.overridePowerPointSessionInitMessage = (sessionInitMessage) => {
            const initMessage = {
                downloadUrl: this.powerpointParameters.downloadUrl,
                getItemsEndPoint: this.powerpointParameters.getItemsEndPoint,
                documentOpenType: this.powerpointParameters.documentOpenType
            };
            for (const key of Object.keys(sessionInitMessage)) {
                initMessage[key] = sessionInitMessage[key];
            }
            return new PowerPointSessionInitMessage(initMessage);
        };
        this.onInitSession = () => {
            if (!this._initSessionCallback) {
                return Promise.resolve();
            }
            return this._initSessionCallback();
        };
        /**
         * Checks params and returns an Error if it is undefined or null; otherwise returns undefined.
         */
        this.checkParameters = (name, params) => {
            if (!params) {
                const errorMessage = `${name} called with invalid parameters`;
                ULS.sendTraceTag(0x2008324b /* tag_6cdjl */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Error, errorMessage);
                return new Error(errorMessage);
            }
            return undefined;
        };
        /**
         * Checks params and throws an Error if it is undefined or null.
         */
        this.checkParametersAndThrow = (name, params) => {
            const paramsError = this.checkParameters(name, params);
            if (paramsError) {
                throw paramsError;
            }
        };
        /**
         * Checks params and throws an Error if it is undefined or null.
         * Checks property and throws an Error if it is truthy, indicating the function was already called.
         */
        this.checkParametersAndPropertyAndThrow = (name, params, property) => {
            this.checkParametersAndThrow(name, params);
            if (property) {
                const errorMessage = `${name} already called`;
                ULS.sendTraceTag(0x2008324c /* tag_6cdjm */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Error, errorMessage);
                throw new Error(errorMessage);
            }
        };
        /**
         * Returns an error if runtime init has not occurred; otherwise returns undefined.
         */
        this.checkRuntimeInit = (name) => {
            if (!this._runtimeInitPromise) {
                const errorMessage = `${name} called before runtime init`;
                ULS.sendTraceTag(0x2008324d /* tag_6cdjn */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Error, errorMessage);
                return new Error(errorMessage);
            }
            return undefined;
        };
        /**
         * Throws an error if runtime init has not occurred.
         */
        this.checkRuntimeInitAndThrow = (name) => {
            const initError = this.checkRuntimeInit(name);
            if (initError) {
                throw initError;
            }
        };
        this.sendBridgeMessage = (bridgeMessage) => {
            this._bridge.sendMessage(JSON.stringify(new BridgeMessage(bridgeMessage)));
        };
        this.onBridgeMessage = (bridgeMessage) => {
            if (this._bridgeInterceptor != null) {
                // if interceptor is registered by host app, forward received message to interceptor and it will be sent back to us inside onBridgeMessageFromInterceptor
                this._bridgeInterceptor.sendMessage(bridgeMessage);
            }
            else {
                this.handleReceivedBridgeMessage(bridgeMessage);
            }
        };
        this.sendSessionInitResponseToBridge = (bridgeId, docSessionId) => {
            if (this._sessionConnectParams && this._settings.sendConnectionParamsAcrossBridge) {
                const sessionUrl = this._sessionConnectParams.sessionUrl;
                this.sendBridgeMessage({
                    bridgeId: bridgeId,
                    docSessionId: docSessionId,
                    message: new SessionInitResponse({
                        origin: this._sessionConnectParams.origin,
                        sessionKey: sessionUrl.split('/').pop(),
                        sessionUrlBase: sessionUrl.split('/').slice(0, -1).join('/'),
                        anonymousToken: this._sessionConnectParams.authToken
                    })
                });
            }
        };
        this.handleReceivedBridgeMessage = (bridgeMessage) => {
            try {
                const { bridgeId, docSessionId, message } = JSON.parse(bridgeMessage);
                const messageTypeName = SchemaObject.getTypeNameFor(message);
                ULS.sendTraceTag(0x1e5821c3 /* tag_4wchd */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Info, `Received ${messageTypeName}`);
                if (this._sessionCloseReason != null) {
                    // if host session was closed earlier, send close message to bridge
                    this.sendBridgeMessage({
                        bridgeId: '*',
                        docSessionId: '*',
                        message: new SessionCloseMessage({ reason: this._sessionCloseReason }),
                    });
                }
                if (AnnotationActivationMessage.typeGuard(message)) {
                    const response = this.activateAnnotation({
                        annotationType: message.annotationType,
                        config: message.config,
                        handler: (op, cv) => {
                            this.sendBridgeMessage({
                                bridgeId,
                                docSessionId,
                                message: new AnnotationResultsMessage({
                                    annotationType: message.annotationType,
                                    ops: [op],
                                    cv: cv
                                })
                            });
                        },
                        forceReturnCachedAnnotations: message.forceReturnCachedAnnotations
                    }).then((result) => {
                        return new AnnotationActivationResponse({ messageId: message.messageId, token: result.token });
                    });
                    this.sendResponseAcrossBridge(response, message, bridgeId, docSessionId);
                }
                else if (SyncMessage.typeGuard(message)) {
                    for (const operation of message.ops) {
                        this.submitOperation({ operation: operation, cv: message.cv });
                    }
                    this.sendBridgeMessage({
                        bridgeId,
                        docSessionId,
                        response: new Response({ messageId: message.messageId }),
                    });
                    ULS.sendTraceTag(0x1e5821c0 /* tag_4wcha */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Info, `Responded to ${messageTypeName}`);
                }
                else if (AnnotationReleaseMessage.typeGuard(message)) {
                    const response = this.releaseAnnotation({ token: message.token })
                        .then((lastRelease) => {
                        return new AnnotationReleaseResponse({ messageId: message.messageId, lastRelease: lastRelease });
                    });
                    this.sendResponseAcrossBridge(response, message, bridgeId, docSessionId);
                }
                else if (SubmittedCustomMessage.typeGuard(message)) {
                    // when host app calls submitCustomMessage using bridge session in SDX, we send it using new type CustomMessageToSubmit
                    this.submitCustomMessage({ message: message.customMessage })
                        .then((response) => {
                        if (Response.typeGuard(response)) {
                            response.messageId = message.messageId;
                            ULS.sendTraceTag(0x1e4e1053 /* tag_4t7bt */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Info, `Responded to ${messageTypeName}`);
                        }
                        else {
                            ULS.sendTraceTag(0x1e4de352 /* tag_4t4ns */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Error, 'Unexpeced response type from SubmitCustomMessage');
                        }
                        this.sendBridgeMessage({
                            bridgeId,
                            docSessionId,
                            response,
                        });
                    })
                        .catch(err => {
                        ULS.sendTraceTag(0x1e4e209c /* tag_4t8c2 */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Error, err.message);
                        this.sendBridgeMessage({
                            bridgeId,
                            docSessionId,
                            response: new ErrorResponse({ messageId: message.messageId, error: err.message }),
                        });
                    });
                }
                else if (SessionInitMessage.typeGuard(message)) {
                    this.sendSessionInitResponseToBridge(bridgeId, docSessionId);
                    this.sendBridgeMessage({
                        bridgeId: bridgeId,
                        docSessionId: docSessionId,
                        message: new ServerAuthenticationStateChangeMessage({ serverAuthenticationState: this._serverAuthenticationState }),
                    });
                }
                else {
                    ULS.sendTraceTag(0x1e5821a3 /* tag_4wcg9 */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Warning, `Did not respond to ${messageTypeName} with Id ${message.messageId}`);
                }
            }
            catch (err) {
                ULS.sendTraceTag(0x1e58220b /* tag_4wcil */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Error, err.message);
                this.sendBridgeMessage({
                    bridgeId: 'unknown',
                    docSessionId: 'unknown',
                    response: new ErrorResponse({ messageId: 'unknown', error: err.message }),
                });
            }
        };
        this.sendResponseAcrossBridge = (response, message, bridgeId, docSessionId) => {
            response.catch((err) => {
                return new ErrorResponse({ messageId: message.messageId, error: err.message });
            }).then((response) => {
                this.sendBridgeMessage({
                    bridgeId,
                    docSessionId,
                    response,
                });
                ULS.sendTraceTag(0x1e55c598 /* tag_4v2wy */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Info, `Responded to ${SchemaObject.getTypeNameFor(message)}`);
            }).catch((err) => {
                ULS.sendTraceTag(0x1e55c597 /* tag_4v2wx */, LogCategory.msoulscat_Wac_BrowserGeneral, TraceLevel.Error, err.message);
            });
        };
        this.onBridgeMessageFromInterceptor = (bridgeMessage) => {
            this.handleReceivedBridgeMessage(bridgeMessage);
        };
    }
    getSessionStateCallbackToken(callbackType) {
        return callbackType + '-callback-' + this._connectionStateCallbackToken++;
    }
}
//# sourceMappingURL=AugLoopRuntimeManager.js.map